import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { Product, ProductCategory } from '@runninglime/shared';
import { apiGet, apiPatch, apiDelete } from '../../lib/api.ts';
import { categoryLabel, formatPrice } from '../../lib/utils.ts';
import { LoadingSpinner } from '../../components/ui/Shared.tsx';

const CATEGORIES: { value: ProductCategory | ''; label: string }[] = [
  { value: '', label: 'All' },
  { value: 'soda', label: '🥤 Soda' },
  { value: 'energy', label: '⚡ Energy' },
  { value: 'brainstorm', label: '🧠 Brainstorm' },
  { value: 'hydrate', label: '💧 Hydrate' },
  { value: 'cold-brew', label: '☕ Cold Brew' },
];

export default function ProductManager() {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [toggling, setToggling] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);

  const load = async () => {
    const res = await apiGet<{ items: Product[]; total: number }>('/admin/products');
    if (res.success && res.data) setProducts(res.data.items);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const toggleActive = async (id: string) => {
    const product = products.find(p => p.id === id);
    if (!product) return;
    setToggling(id);
    await apiPatch(`/admin/products/${id}`, { active: !product.active });
    await load();
    setToggling(null);
  };

  const deleteProduct = async (id: string, name: string) => {
    if (!confirm(`Are you sure you want to permanently delete "${name}"? This cannot be undone.`)) return;
    setDeleting(id);
    await apiDelete(`/admin/products/${id}`);
    await load();
    setDeleting(null);
  };

  const filtered = filter ? products.filter((p) => p.category === filter) : products;

  if (loading) return <LoadingSpinner />;

  return (
    <div className="animate-fade-in">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
        <h1 className="text-3xl font-bold text-dark">Products</h1>
        <Link to="/admin/products/new" className="btn-gradient px-5 py-2.5 rounded-xl text-sm font-semibold">
          Add Product
        </Link>
      </div>

      <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
        {CATEGORIES.map((c) => (
          <button
            key={c.value}
            onClick={() => setFilter(c.value)}
            className={`px-4 py-2 rounded-xl text-sm font-medium whitespace-nowrap transition-all ${
              filter === c.value
                ? 'bg-lime text-dark shadow-md'
                : 'bg-white text-gray-600 hover:bg-lime/20 border border-gray-200'
            }`}
          >
            {c.label}
          </button>
        ))}
      </div>

      <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-100">
                <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">Product</th>
                <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">Category</th>
                <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">Variants</th>
                <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="text-right px-6 py-3 text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {filtered.map((p) => (
                <tr key={p.id} className={`hover:bg-gray-50/80 transition-colors duration-200 ${!p.active ? 'opacity-50' : ''}`}>
                  <td className="px-6 py-4">
                    <span className="font-medium text-dark">{p.name}</span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">{categoryLabel(p.category)}</td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {p.variants.length} ({p.variants.map((v) => v.size).join(', ')})
                  </td>
                  <td className="px-6 py-4">
                    <button
                      onClick={() => toggleActive(p.id)}
                      disabled={toggling === p.id}
                      className={`px-3 py-1 rounded-full text-xs font-medium transition-all duration-200 ${
                        p.active
                          ? 'bg-green/10 text-green hover:bg-green/20'
                          : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                      }`}
                    >
                      {toggling === p.id ? '...' : p.active ? 'Active' : 'Inactive'}
                    </button>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex items-center justify-end gap-3">
                      <Link
                        to={`/admin/products/${p.id}`}
                        className="text-sm text-green hover:underline"
                      >
                        Edit
                      </Link>
                      <button
                        onClick={() => deleteProduct(p.id, p.name)}
                        disabled={deleting === p.id}
                        className="text-sm text-red-500 hover:underline disabled:opacity-50"
                      >
                        {deleting === p.id ? '...' : 'Delete'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
